import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Settings } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import AccountsTab from "./components/AccountsTab";
import GeneralTab from "./components/GeneralTab";
import ResetTab from "./components/ResetTab";
import TestingTab from "./components/TestingTab";
import { useDevSettings } from "./hooks/useDevSettings";
import { useResetOptions } from "./hooks/useResetOptions";
import { useTestApplications } from "./hooks/useTestApplications";

const DevSettings = ({
   brightness,
   setBrightness,
   typewriterEnabled,
   setTypewriterEnabled,
   setTheme
}) => {
   const [currentTheme, setCurrentTheme] = useState(
      document.documentElement.classList.contains('dark') ? 'dark' : 'light'
   );
   const [showResetAllDialog, setShowResetAllDialog] = useState(false);
   const [showUnsubmitAllDialog, setShowUnsubmitAllDialog] = useState(false);
   const [open, setOpen] = useState(false);
   const dialogTriggerRef = useRef(null);
   const [autoCheckEnabled, setAutoCheckEnabled] = useState(() => {
      return localStorage.getItem('autoCheckEnabled') === 'true';
   });
   const [compactMenuEnabled, setCompactMenuEnabled] = useState(() => {
      return localStorage.getItem('compactMenuEnabled') === 'true';
   });
   const [verificationEnabled, setVerificationEnabled] = useState(() => {
      return localStorage.getItem('verificationEnabled') !== 'false'; // Default to true
   });
   const [pltpVerificationEnabled, setPLTPVerificationEnabled] = useState(() => {
      return localStorage.getItem('pltpVerificationEnabled') !== 'false'; // Default to true
   });
   const [csawValidationEnabled, setCSAWValidationEnabled] = useState(() => {
      return localStorage.getItem('csawValidationEnabled') !== 'false'; // Default to true
   });
   const [covValidationEnabled, setCOVValidationEnabled] = useState(() => {
      return localStorage.getItem('covValidationEnabled') !== 'false'; // Default to true
   });
   const [ptprValidationEnabled, setPTPRValidationEnabled] = useState(() => {
      return localStorage.getItem('ptprValidationEnabled') !== 'false'; // Default to true
   });
   const [mockFinancialDataEnabled, setMockFinancialDataEnabled] = useState(() => {
      return localStorage.getItem('useMockFinancialData') === 'true';
   });

   // Role to label mapping for keyboard shortcuts
   const roleToLabel = {
      'user': 'Client User',
      'Technical_Staff': 'Technical Staff',
      'Chief_RPS': 'Chief RPS',
      'Chief_TSD': 'Chief TSD',
      'Bill_Collector': 'Bill Collector',
      'Forest_Ranger': 'Forest Ranger',
      'PENR_CENR_Officer': 'PENR/CENR Officer',
      'Releasing_Clerk': 'Releasing Clerk',
      'superadmin': 'Superadmin'
   };

   // Theme handlers
   const handleThemeToggle = useCallback((checked) => {
      const newTheme = checked ? 'dark' : 'light';
      setCurrentTheme(newTheme);
      setTheme(newTheme);
   }, [setTheme]);

   const handleBrightnessChange = useCallback((value) => {
      setBrightness(value[0]);
   }, [setBrightness]);

   const handleAutoCheckToggle = useCallback((checked) => {
      setAutoCheckEnabled(checked);
      localStorage.setItem('autoCheckEnabled', checked);
      toast.success(`Auto-check ${checked ? 'enabled' : 'disabled'}`, {
         description: `Authenticity review and release confirmation checkboxes will be ${checked ? 'automatically checked' : 'unchecked by default'}`
      });
   }, []);

   const handleCompactMenuToggle = useCallback((checked) => {
      setCompactMenuEnabled(checked);
      localStorage.setItem('compactMenuEnabled', checked);
      toast.success(`${checked ? 'Expanded' : 'Compact'} menu enabled`, {
         description: `Using ${checked ? 'action buttons' : '3-dot menu'} for application actions`
      });
   }, []);

   const handleVerificationToggle = useCallback((checked) => {
      setVerificationEnabled(checked);
      localStorage.setItem('verificationEnabled', checked);
      toast.success(`Inventory verification ${checked ? 'enabled' : 'disabled'}`, {
         description: `Application verification is now ${checked ? 'required' : 'optional'} for cutting/earth balling requests`
      });
   }, []);

   const handlePLTPVerificationToggle = useCallback((checked) => {
      setPLTPVerificationEnabled(checked);
      localStorage.setItem('pltpVerificationEnabled', checked);
      toast.success(`PLTP verification ${checked ? 'enabled' : 'disabled'}`, {
         description: `PLTP permit verification is now ${checked ? 'required' : 'optional'} for COV applications`
      });
   }, []);

   const handleCSAWValidationToggle = useCallback((checked) => {
      setCSAWValidationEnabled(checked);
      localStorage.setItem('csawValidationEnabled', checked);
      toast.success(`CSAW form validation ${checked ? 'enabled' : 'disabled'}`, {
         description: `Form validation is now ${checked ? 'enabled' : 'disabled'} for chainsaw registration`
      });
   }, []);

   const handleCOVValidationToggle = useCallback((checked) => {
      setCOVValidationEnabled(checked);
      localStorage.setItem('covValidationEnabled', checked);
      toast.success(`COV form validation ${checked ? 'enabled' : 'disabled'}`, {
         description: `Form validation is now ${checked ? 'enabled' : 'disabled'} for certificate of verification`
      });
   }, []);

   const handlePTPRValidationToggle = useCallback((checked) => {
      setPTPRValidationEnabled(checked);
      localStorage.setItem('ptprValidationEnabled', checked);
      toast.success(`PTPR form validation ${checked ? 'enabled' : 'disabled'}`, {
         description: `Form validation is now ${checked ? 'enabled' : 'disabled'} for private tree plantation registration`
      });
   }, []);

   const handleMockFinancialDataToggle = useCallback((checked) => {
      setMockFinancialDataEnabled(checked);
      localStorage.setItem('useMockFinancialData', checked.toString());
      toast.success(`Mock financial data ${checked ? 'enabled' : 'disabled'}`, {
         description: `Financial analytics will now use ${checked ? 'mock' : 'real'} data`
      });
   }, []);

   const handleTypewriterToggle = useCallback((checked) => {
      setTypewriterEnabled(checked);
      localStorage.setItem('typewriterEnabled', checked.toString());
      toast.success(`Typewriter effect ${checked ? 'enabled' : 'disabled'}`, {
         description: `Descriptions will ${checked ? 'animate' : 'appear instantly'}`
      });
   }, [setTypewriterEnabled]);

   // Get current user label for accounts tab
   const getCurrentUserLabel = useCallback(() => {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user.username ? `${user.username} (${user.roles?.[0] || 'N/A'})` : 'Not logged in';
   }, []);

   // Predefined accounts for quick switching
   const predefinedAccounts = [
      { username: 'mark_gilbert_reginio', password: 'password123D', label: 'Client User' },
      { username: 'Technical_Staff', password: 'password123', label: 'Technical Staff' },
      { username: 'Chief_RPS', password: 'password123', label: 'Chief RPS' },
      { username: 'Chief_TSD', password: 'password123', label: 'Chief TSD' },
      { username: 'Bill_Collector', password: 'password123', label: 'Bill Collector' },
      { username: 'Forest_Ranger', password: 'password123', label: 'Forest Ranger' },
      { username: 'PENR_Officer', password: 'password123', label: 'PENR/CENR Officer' },
      { username: 'Releasing_Clerk', password: 'password123', label: 'Releasing Clerk' },
      { username: 'super_admin', password: 'password123', label: 'Superadmin' },
   ];

   const { handleQuickSwitch } = useDevSettings();

   // Keyboard shortcuts
   useEffect(() => {
      const handleKeyDown = (event) => {
         // Open dev settings with Shift + O
         if (event.altKey && event.shiftKey && event.key.toLowerCase() === 'o') {
            event.preventDefault(); // prevent the default behavior of the event because it will open the dev settings if not prevented
            setOpen(prev => !prev); // for opening and closing the dev settings, prev is the current state and !prev is the opposite state
            toast.success('Dev Settings ' + (!open ? 'Opened' : 'Closed'), {
               description: 'Using Alt + Shift + O'
            });
         }

         // Switch to client user
         if (event.altKey && event.key === '1') {
            handleQuickSwitch(predefinedAccounts[0]);
         }
         if (event.altKey && event.key === '2') {
            handleQuickSwitch(predefinedAccounts[1]);
         }
         if (event.altKey && event.key === '3') {
            handleQuickSwitch(predefinedAccounts[5]); // Forest Ranger
         }
         if (event.altKey && event.key === '4') {
            handleQuickSwitch(predefinedAccounts[3]); // Chief TSD
         }
         if (event.altKey && event.key === '5') {
            handleQuickSwitch(predefinedAccounts[6]); // PENR/CENR Officer
         }
         if (event.altKey && event.key === '6') {
            handleQuickSwitch(predefinedAccounts[4]); // Bill Collector
         }

         // Check if Ctrl key is pressed with Arrow Up/Down
         if (event.ctrlKey && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
            event.preventDefault();

            const currentUser = JSON.parse(localStorage.getItem('user'));
            if (!currentUser) return;

            // Find current account index
            const currentIndex = predefinedAccounts.findIndex(account => {
               const userRole = currentUser.roles[0];
               return roleToLabel[userRole] === account.label;
            });

            if (currentIndex === -1) return;

            // Calculate next account index
            let nextIndex;
            if (event.key === 'ArrowDown') {
               nextIndex = (currentIndex + 1) % predefinedAccounts.length;
            } else {
               nextIndex = (currentIndex - 1 + predefinedAccounts.length) % predefinedAccounts.length;
            }

            // Switch to the next account
            handleQuickSwitch(predefinedAccounts[nextIndex]);

            // Show toast notification
            // toast.success(`Switched to ${predefinedAccounts[nextIndex].label}`, {
            //    description: `Using ${event.key === 'ArrowDown' ? 'Ctrl + ↓' : 'Ctrl + ↑'}`
            // });
         }

         // Handle brightness control
         if (event.ctrlKey && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
            event.preventDefault();

            const step = 5; // Brightness adjustment step
            const newBrightness = event.key === 'ArrowRight'
               ? Math.min(100, brightness + step)
               : Math.max(30, brightness - step);

            setBrightness(newBrightness);

            toast.success(`Brightness: ${newBrightness}%`, {
               description: `Using ${event.key === 'ArrowRight' ? 'Ctrl + →' : 'Ctrl + ←'}`
            });
         }
      };

      window.addEventListener('keydown', handleKeyDown);

      // Cleanup
      return () => {
         window.removeEventListener('keydown', handleKeyDown);
      };
   }, [brightness, setBrightness, handleQuickSwitch, predefinedAccounts, roleToLabel, open]);

   const {
      handleCreateTestCSAW,
      handleCreateTestCOV,
      handleCreateTestPTPR,
      handleCreateTestPLTCP,
      handleCreateTestPLTP,
      handleCreateTestTCEBP,
      handleCreateTestOOP,
      refetchOOPs,
      exportFinancialDataJSON
   } = useTestApplications();
   const {
      searchTerm,
      setSearchTerm,
      isResettingSpecific,
      applicationNumbersLoading,
      filteredApplications,
      handleResetSpecific,
      resetAllApplications,
      unsubmitAllApplications,
      handleResetDatabase,
      handleDeleteAllApplications,
      handleDeleteAllUserNotifications,
      handleDeleteAllPersonnelNotifications
   } = useResetOptions();

   return (
      <Dialog open={open} onOpenChange={setOpen}>
         <DialogTrigger asChild>
            <Button ref={dialogTriggerRef} variant="ghost" size="sm" className="flex items-center gap-2">
               <Settings className="h-4 w-4" />
               <span className="text-sm font-medium">Dev Settings</span>
            </Button>
         </DialogTrigger>
         <DialogContent className="sm:max-w-[800px] max-h-[80vh] overflow-y-auto pr-2 mt-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
            <DialogHeader>
               <DialogTitle>Developer Settings</DialogTitle>
               <DialogDescription>
                  This is a developer settings page.
               </DialogDescription>
            </DialogHeader>

            <Tabs defaultValue="testing" className="w-full">
               <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="general">General</TabsTrigger>
                  <TabsTrigger value="accounts">Accounts</TabsTrigger>
                  <TabsTrigger value="testing">Testing</TabsTrigger>
                  <TabsTrigger value="reset">Reset</TabsTrigger>
               </TabsList>

               <TabsContent value="general">
                  <GeneralTab
                     currentTheme={currentTheme}
                     handleThemeToggle={handleThemeToggle}
                     typewriterEnabled={typewriterEnabled}
                     handleTypewriterToggle={handleTypewriterToggle}
                     brightness={brightness}
                     handleBrightnessChange={handleBrightnessChange}
                     autoCheckEnabled={autoCheckEnabled}
                     handleAutoCheckToggle={handleAutoCheckToggle}
                     compactMenuEnabled={compactMenuEnabled}
                     handleCompactMenuToggle={handleCompactMenuToggle}
                     verificationEnabled={verificationEnabled}
                     handleVerificationToggle={handleVerificationToggle}
                     pltpVerificationEnabled={pltpVerificationEnabled}
                     handlePLTPVerificationToggle={handlePLTPVerificationToggle}
                     csawValidationEnabled={csawValidationEnabled}
                     handleCSAWValidationToggle={handleCSAWValidationToggle}
                     covValidationEnabled={covValidationEnabled}
                     handleCOVValidationToggle={handleCOVValidationToggle}
                     ptprValidationEnabled={ptprValidationEnabled}
                     handlePTPRValidationToggle={handlePTPRValidationToggle}
                     mockFinancialDataEnabled={mockFinancialDataEnabled}
                     handleMockFinancialDataToggle={handleMockFinancialDataToggle}
                  />
               </TabsContent>

               <TabsContent value="accounts">
                  <AccountsTab
                     getCurrentUserLabel={getCurrentUserLabel}
                     predefinedAccounts={predefinedAccounts}
                     handleQuickSwitch={handleQuickSwitch}
                  />
               </TabsContent>

               <TabsContent value="testing">
                  <TestingTab
                     handleCreateTestCSAW={handleCreateTestCSAW}
                     handleCreateTestCOV={handleCreateTestCOV}
                     handleCreateTestPTPR={handleCreateTestPTPR}
                     handleCreateTestPLTP={handleCreateTestPLTP}
                     handleCreateTestPLTCP={handleCreateTestPLTCP}
                     handleCreateTestTCEBP={handleCreateTestTCEBP}
                     handleCreateTestOOP={handleCreateTestOOP}
                     refetchOOPs={refetchOOPs}
                     exportFinancialDataJSON={exportFinancialDataJSON}
                  />
               </TabsContent>

               <TabsContent value="reset" className="space-y-4">
                  <ResetTab
                     searchTerm={searchTerm}
                     setSearchTerm={setSearchTerm}
                     filteredApplications={filteredApplications}
                     applicationNumbersLoading={applicationNumbersLoading}
                     isResettingSpecific={isResettingSpecific}
                     handleResetSpecific={handleResetSpecific}
                     setShowResetAllDialog={setShowResetAllDialog}
                     setShowUnsubmitAllDialog={setShowUnsubmitAllDialog}
                     handleResetDatabase={handleResetDatabase}
                     handleDeleteAllApplications={handleDeleteAllApplications}
                     handleDeleteAllUserNotifications={handleDeleteAllUserNotifications}
                     handleDeleteAllPersonnelNotifications={handleDeleteAllPersonnelNotifications}
                  />
               </TabsContent>
            </Tabs>
         </DialogContent>
      </Dialog>
   );
};

export default DevSettings;